/**
 * Appends subsequent collection pages into #product-grid via the Section Rendering API.
 * Re-initializes when the grid is replaced (e.g. facet filters) because connectedCallback runs again.
 */
class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    this.sectionId = this.dataset.sectionId;
    this.statusEl = document.getElementById('CollectionInfiniteScrollStatus');
    this.isLoading = false;
    if (!this.grid || !this.sectionId) return;
    if (!this.dataset.nextUrl) return;
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
  }

  setupIntersectionObserver() {
    if (this._io) this._io.disconnect();
    this._io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target === this && !this.isLoading) {
            this.loadNext();
          }
        });
      },
      { root: null, rootMargin: '500px 0px 400px 0px', threshold: 0 }
    );
    this._io.observe(this);
  }

  runCardScripts(container) {
    container.querySelectorAll('script[data-card-id]').forEach((script) => {
      try {
        const s = document.createElement('script');
        s.textContent = script.textContent;
        document.body.appendChild(s);
        document.body.removeChild(s);
      } catch (e) {
        /* ignore */
      }
    });
  }

  async loadNext() {
    const next = this.dataset.nextUrl;
    if (!next || this.isLoading || !this.grid) return;

    this.isLoading = true;
    if (this.statusEl) this.statusEl.textContent = 'Loading more products';

    try {
      const url = new URL(next, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(String(response.status));
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.getElementById('product-grid');
      const newSentinel = doc.querySelector('collection-infinite-scroll');
      if (!newGrid) {
        if (this.statusEl) this.statusEl.textContent = '';
        return;
      }

      newGrid.querySelectorAll(':scope > li.grid__item').forEach((li) => {
        const clone = document.importNode(li, true);
        this.grid.appendChild(clone);
        this.runCardScripts(clone);
      });

      const nextUrl = newSentinel?.dataset?.nextUrl || '';
      if (nextUrl) {
        this.dataset.nextUrl = nextUrl;
      } else {
        delete this.dataset.nextUrl;
        if (this._io) {
          this._io.disconnect();
          this._io = null;
        }
        this.setAttribute('hidden', '');
      }
      if (this.statusEl) this.statusEl.textContent = '';
    } catch (e) {
      if (this.statusEl) this.statusEl.textContent = 'Could not load more products';
      console.error('[collection-infinite-scroll]', e);
    } finally {
      this.isLoading = false;
    }
  }
}

if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
